<script setup>
import { ref, reactive, computed } from "vue";
import { apiQuery } from "@/api/j24api";
import { fetchAuthSession } from "aws-amplify/auth";

const config = reactive({
  url: "",
  username: "",
  password: "",
  portalUser: "",
});

const currentStep = ref(1);
const loadingButton = ref(false);
const loadingObjects = ref(false);
const loadingDependencies = ref(false);
const loadingExport = ref(false);

const showFailedCredentialDialog = ref(false);
const showWarningDialog = ref(false);
const showCompletionDialog = ref(false);
const showCompletionWarningDialog = ref(false);

const search = ref("");
const selectedTypeFilter = ref("All");

// Jamf object types and display names
const objectTypeDisplayNames = {
  policies: "Policies",
  scripts: "Scripts",
  packages: "Packages",
  computergroups: "Computer Groups",
  mobiledevicegroups: "Mobile Groups",
  osxconfigurationprofiles: "macOS Configuration Profiles",
  mobiledeviceconfigurationprofiles: "Mobile Configuration Profiles",
  computerextensionattributes: "macOS Extension Attributes",
  mobiledeviceextensionattributes: "Mobile Extension Attributes",
  categories: "Categories",
  dockitems: "Dock Items",
  diskencryptionconfigurations: "Disk Encryption Configurations",
  restrictedsoftware: "Restricted Software",
  advancedcomputersearches: "Advanced Computer Searches",
  advancedmobiledevicesearches: "Advanced Mobile Searches",
  networksegments: "Network Segments",
  "/v3/computer-prestages": "Computer Prestages",
  "/v2/mobile-device-prestages": "Mobile Device Prestages",
};

// Available objects from Jamf, keyed by type
const availableObjects = ref({});
// Flat list of all objects for the data table
const objectTableItems = ref([]);
// Selected items
const selected = ref([]);
// Dependency tree
const dependencyTree = ref({});
// Final export list with status tracking
const exportItems = ref([]);

const steps = [
  { title: "Jamf Credentials", value: 1, complete: false },
  { title: "Select Objects", value: 2, complete: false },
  { title: "Dependencies", value: 3, complete: false },
  { title: "Review & Export", value: 4, complete: false },
];

const objectHeaders = [
  { title: "Name", value: "name", sortable: true, width: "50%" },
  { title: "Type", value: "typeDisplay", sortable: true, width: "50%" },
];

const dependencyHeaders = [
  { title: "Name", value: "name", sortable: true, width: "50%" },
  { title: "Type", value: "typeDisplay", sortable: true, width: "50%" },
];

const exportHeaders = [
  { title: "Name", value: "name", width: "40%" },
  { title: "Type", value: "typeDisplay", width: "30%" },
  { title: "Status", value: "status", width: "20%" },
  { title: "Remove", value: "actions", width: "10%" },
];

const itemsPerPage = [
  { value: 25, title: "25" },
  { value: 50, title: "50" },
  { value: 100, title: "100" },
  { value: -1, title: "All" },
];

const typeFilterOptions = computed(() => {
  const types = new Set(objectTableItems.value.map((item) => item.typeDisplay));
  return ["All", ...Array.from(types).sort()];
});

const filteredObjects = computed(() => {
  let items = objectTableItems.value;
  if (selectedTypeFilter.value !== "All") {
    items = items.filter(
      (item) => item.typeDisplay === selectedTypeFilter.value
    );
  }
  return items;
});

async function getPortalUser() {
  try {
    const session = await fetchAuthSession();
    return session.tokens.idToken.payload.email;
  } catch {
    return "Unknown";
  }
}

async function verifyCredentials() {
  loadingButton.value = true;

  config.portalUser = await getPortalUser();

  const result = await apiQuery("ELA", "mdmTools/command", {
    mdm: "jamf",
    command: "credentialsCheck",
    config: {
      url: config.url.replace(/\/$/, ""),
      username: config.username,
      password: config.password,
    },
  });

  if (result === "Success") {
    await loadAvailableObjects();
    nextStep();
  } else {
    showFailedCredentialDialog.value = true;
  }

  loadingButton.value = false;
}

async function loadAvailableObjects() {
  loadingObjects.value = true;

  const result = await apiQuery("ELA", "mdmTools/command", {
    mdm: "jamf",
    command: "getAvailableObjects",
    config: {
      url: config.url.replace(/\/$/, ""),
      username: config.username,
      password: config.password,
    },
  });

  if (typeof result === "object") {
    availableObjects.value = result;

    // Build flat table items
    objectTableItems.value = [];
    let idCounter = 0;
    for (const [type, items] of Object.entries(result)) {
      for (const name of items) {
        objectTableItems.value.push({
          id: idCounter++,
          name: name,
          type: type,
          typeDisplay: objectTypeDisplayNames[type] || type,
        });
      }
    }
  }

  loadingObjects.value = false;
}

async function resolveDependencies() {
  loadingDependencies.value = true;

  // Build the import array from selected items
  const importArr = {};
  for (const id of selected.value) {
    const item = objectTableItems.value.find((obj) => obj.id === id);
    if (item) {
      if (!importArr[item.type]) {
        importArr[item.type] = [];
      }
      importArr[item.type].push(item.name);
    }
  }

  const result = await apiQuery("ELA", "mdmTools/command", {
    mdm: "jamf",
    command: "buildDependencies",
    config: {
      url: config.url.replace(/\/$/, ""),
      username: config.username,
      password: config.password,
    },
    importArr: importArr,
  });

  if (typeof result === "object") {
    dependencyTree.value = result;

    // Build the export items list from the dependency tree
    exportItems.value = [];
    let exportId = 0;
    for (const [type, items] of Object.entries(result)) {
      for (const name of items) {
        exportItems.value.push({
          id: exportId++,
          name: name,
          type: type,
          typeDisplay: objectTypeDisplayNames[type] || type,
          status: "Pending",
        });
      }
    }
  }

  loadingDependencies.value = false;
}

function nextStep() {
  if (currentStep.value < steps.length) {
    steps[currentStep.value - 1].complete = true;

    if (currentStep.value === 2) {
      if (selected.value.length === 0) {
        showWarningDialog.value = true;
        return;
      }
    }

    currentStep.value++;

    // Auto-resolve dependencies when moving to step 3
    if (currentStep.value === 3) {
      resolveDependencies();
    }
  }
}

function prevStep() {
  if (currentStep.value > 1) {
    currentStep.value--;
    steps[currentStep.value - 1].complete = false;
  }
}

function deleteExportItem(item) {
  const index = exportItems.value.indexOf(item);
  if (index > -1) {
    exportItems.value.splice(index, 1);
  }
}

async function runExport() {
  loadingExport.value = true;

  const itemsToExport = exportItems.value.map((item) => ({
    itemName: item.name,
    itemType: item.type,
  }));

  for (const item of exportItems.value) {
    item.status = "In Progress";

    const result = await apiQuery("ELA", "mdmTools/command", {
      mdm: "jamf",
      command: "export",
      config: {
        url: config.url.replace(/\/$/, ""),
        username: config.username,
        password: config.password,
        portalUser: config.portalUser,
      },
      items: [{ itemName: item.name, itemType: item.type }],
    });

    if (result && Array.isArray(result)) {
      const itemResult = result[0];
      if (itemResult && itemResult.status === "Success") {
        item.status = "Complete";
      } else {
        item.status = "Error";
      }
    } else {
      item.status = "Error";
    }
  }

  // Send log
  await apiQuery("ELA", "mdmTools/command", {
    mdm: "jamf",
    command: "log",
    payload: {
      mdm: "Jamf",
      logType: "Export",
      jamfInstance: config.url,
      jamfApiUser: config.username,
      portalUser: config.portalUser,
      timestamp: new Date().toISOString(),
      log: exportItems.value.map((item) => ({
        name: item.name,
        type: item.type,
        status: item.status,
      })),
    },
  });

  loadingExport.value = false;

  // Show completion dialog
  if (exportItems.value.some((item) => item.status === "Error")) {
    showCompletionWarningDialog.value = true;
  } else {
    showCompletionDialog.value = true;
  }

  steps[steps.length - 1].complete = true;
}

function changeStatusColour(status) {
  switch (status) {
    case "Pending":
      return "grey";
    case "In Progress":
      return "orange";
    case "Complete":
      return "green";
    case "Error":
      return "red";
    default:
      return "grey";
  }
}

const dependencyTableItems = computed(() => {
  const items = [];
  for (const [type, names] of Object.entries(dependencyTree.value)) {
    for (const name of names) {
      // Check if this was originally selected by the user
      const wasSelected = selected.value.some((id) => {
        const obj = objectTableItems.value.find((o) => o.id === id);
        return obj && obj.name === name && obj.type === type;
      });

      items.push({
        name: name,
        type: type,
        typeDisplay: objectTypeDisplayNames[type] || type,
        isDependency: !wasSelected,
      });
    }
  }
  return items;
});
</script>

<template>
  <v-stepper v-model="currentStep">
    <v-alert
      v-if="currentStep > 1"
      type="info"
      variant="tonal"
      density="compact"
      class="mx-4 mt-3"
    >
      <span class="font-weight-bold">Connected to:</span> {{ config.url }}
    </v-alert>
    <v-stepper-header>
      <v-stepper-item
        v-for="(step, index) in steps"
        :key="index"
        :title="step.title"
        :value="step.value"
        :complete="step.complete"
      ></v-stepper-item>
    </v-stepper-header>
    <v-stepper-content
      v-for="(step, index) in steps"
      :key="index"
      :step="step.value"
    >
      <v-sheet v-if="step.value === currentStep">
        <v-card-text>
          <!-- Step 1: Jamf Credentials -->
          <template v-if="step.value === 1">
            <v-card max-width="500" variant="elevated" class="mx-auto">
              <v-card-text>
                <v-table class="text-left">
                  <v-responsive class="mx-auto py-2" max-width="500">
                    <v-text-field
                      clearable
                      v-model="config.url"
                      label="Jamf Pro URL"
                      type="input"
                      hint="e.g. https://yourinstance.jamfcloud.com"
                      variant="outlined"
                    ></v-text-field>
                  </v-responsive>
                  <v-responsive class="mx-auto py-2" max-width="500">
                    <v-text-field
                      clearable
                      v-model="config.username"
                      label="API Username"
                      type="input"
                      variant="outlined"
                    ></v-text-field>
                  </v-responsive>
                  <v-responsive class="mx-auto py-2" max-width="500">
                    <v-text-field
                      clearable
                      v-model="config.password"
                      label="API Password"
                      type="password"
                      variant="outlined"
                    ></v-text-field>
                  </v-responsive>
                  <v-btn
                    block
                    class="text-none mb-4"
                    size="x-large"
                    @click="verifyCredentials"
                    :disabled="
                      !config.url ||
                      !config.username ||
                      !config.password ||
                      loadingButton
                    "
                    :loading="loadingButton"
                    variant="tonal"
                    color="darkgrey"
                    text="Validate & Load Objects"
                  ></v-btn>
                </v-table>
              </v-card-text>
            </v-card>
          </template>

          <!-- Step 2: Select Objects -->
          <template v-if="step.value === 2">
            <h2>Select Jamf objects to export</h2>
            <v-responsive>
              <div class="d-flex">
                <v-text-field
                  v-model="search"
                  append-icon="mdi-magnify"
                  label="Search"
                  variant="underlined"
                  class="input-field"
                  style="margin-right: 20px"
                ></v-text-field>
                <v-select
                  v-model="selectedTypeFilter"
                  :items="typeFilterOptions"
                  label="Filter by Type"
                  variant="underlined"
                  class="input-field"
                  style="max-width: 300px"
                ></v-select>
              </div>
            </v-responsive>
            <v-data-table
              :headers="objectHeaders"
              :items="filteredObjects"
              :search="search"
              item-key="id"
              show-select
              v-model="selected"
              class="elevation-2"
              :items-per-page="25"
              :items-per-page-options="itemsPerPage"
              :loading="loadingObjects"
            ></v-data-table>
          </template>

          <!-- Step 3: Dependencies -->
          <template v-if="step.value === 3">
            <h2>Resolved Dependencies</h2>
            <p class="text-body-2 mb-4">
              The following objects and their dependencies will be exported.
              Items marked as
              <v-chip size="x-small" color="blue" class="mx-1"
                >dependency</v-chip
              >
              were automatically added.
            </p>
            <v-data-table
              v-if="!loadingDependencies"
              :headers="dependencyHeaders"
              :items="dependencyTableItems"
              item-key="name"
              class="elevation-2"
              :items-per-page="-1"
              :items-per-page-options="[{ value: -1, title: 'All' }]"
            >
              <template #[`item.name`]="{ item }">
                {{ item.name }}
                <v-chip
                  v-if="item.isDependency"
                  size="x-small"
                  color="blue"
                  class="ml-2"
                >
                  dependency
                </v-chip>
              </template>
            </v-data-table>
            <div v-else class="text-center py-8">
              <v-progress-circular
                indeterminate
                color="primary"
                size="64"
              ></v-progress-circular>
              <p class="mt-4">Resolving dependencies...</p>
            </div>
          </template>

          <!-- Step 4: Review & Export -->
          <template v-if="step.value === 4">
            <h2>Review and export</h2>
            <div style="margin-bottom: 10px"></div>
            <v-data-table
              :headers="exportHeaders"
              :items="exportItems"
              item-key="id"
              :items-per-page="-1"
              :items-per-page-options="[{ value: -1, title: 'All' }]"
              class="elevation-2"
            >
              <template #[`item.status`]="{ item }">
                <v-chip :color="changeStatusColour(item.status)">
                  {{ item.status }}
                </v-chip>
              </template>
              <template #[`item.actions`]="{ item }">
                <v-icon
                  v-if="item.status === 'Pending'"
                  @click="deleteExportItem(item)"
                >
                  mdi-delete
                </v-icon>
              </template>
            </v-data-table>
          </template>
        </v-card-text>
        <v-card-actions>
          <v-btn
            v-if="currentStep > 1"
            variant="elevated"
            @click="prevStep"
            class="mr-auto"
            color="rgb(247, 247, 247)"
          >
            Back
          </v-btn>
          <v-btn
            v-if="currentStep > 1 && currentStep < 4"
            variant="elevated"
            @click="nextStep"
            color="primary"
            :disabled="loadingDependencies"
          >
            Next
          </v-btn>
          <v-btn
            v-if="currentStep === 4"
            @click="runExport"
            variant="elevated"
            color="primary"
            :loading="loadingExport"
            :disabled="loadingExport || exportItems.length === 0"
          >
            Export to Templates
          </v-btn>
        </v-card-actions>
      </v-sheet>

      <!-- Dialogs -->
      <v-dialog v-model="showFailedCredentialDialog" max-width="500" persistent>
        <div class="text-center">
          <v-card>
            <div class="py-12 text-center">
              <v-icon
                class="mb-6"
                color="warning"
                icon="mdi-alert-circle-outline"
                size="128"
              ></v-icon>
              <div class="text-h4 font-weight-bold">
                Credential Check Failed
              </div>
              <div>Verify the Jamf Pro URL and credentials are correct</div>
            </div>
            <v-divider></v-divider>
            <div class="pa-4 text-center">
              <v-btn
                color="medium-emphasis"
                min-width="200"
                variant="outlined"
                @click="showFailedCredentialDialog = false"
              >
                Close
              </v-btn>
            </div>
          </v-card>
        </div>
      </v-dialog>

      <v-dialog v-model="showWarningDialog" max-width="500" persistent>
        <div class="text-center">
          <v-card>
            <v-card-text
              ><h3>
                Please select at least one object before proceeding
              </h3></v-card-text
            >
            <v-divider></v-divider>
            <v-card-actions>
              <v-btn
                class="flex-grow-1"
                variant="tonal"
                @click="showWarningDialog = false"
                >OK</v-btn
              >
            </v-card-actions>
          </v-card>
        </div>
      </v-dialog>

      <v-dialog v-model="showCompletionDialog" max-width="500" persistent>
        <div class="text-center">
          <v-card>
            <div class="py-12 text-center">
              <v-icon
                class="mb-6"
                color="success"
                icon="mdi-check-circle-outline"
                size="128"
              ></v-icon>
              <div class="text-h4 font-weight-bold">Export Completed</div>
            </div>
            <v-divider></v-divider>
            <div class="pa-4 text-center">
              <v-btn
                color="medium-emphasis"
                min-width="200"
                variant="outlined"
                @click="showCompletionDialog = false"
              >
                Close
              </v-btn>
            </div>
          </v-card>
        </div>
      </v-dialog>

      <v-dialog
        v-model="showCompletionWarningDialog"
        max-width="500"
        persistent
      >
        <div class="text-center">
          <v-card>
            <div class="py-12 text-center">
              <v-icon
                class="mb-6"
                color="warning"
                icon="mdi-alert-circle-outline"
                size="128"
              ></v-icon>
              <div class="text-h4 font-weight-bold">Completed with Errors</div>
              <div style="padding: 0 20px">
                Some items failed to export. Check the status and try again.
              </div>
            </div>
            <v-divider></v-divider>
            <div class="pa-4 text-center">
              <v-btn
                color="medium-emphasis"
                min-width="200"
                variant="outlined"
                @click="showCompletionWarningDialog = false"
              >
                Close
              </v-btn>
            </div>
          </v-card>
        </div>
      </v-dialog>
    </v-stepper-content>
  </v-stepper>
</template>

<style scoped>
.input-field {
  width: 50%;
}
</style>
